import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  SpeechRecognitionStatus,
  SpeechRecognitionError,
  SpeechRecognitionErrorCode,
  UseSpeechRecognitionOptions,
  UseSpeechRecognitionReturn,
  WebSpeechRecognition,
  WebSpeechRecognitionEvent,
  WebSpeechRecognitionErrorEvent,
} from '../types/speech';

const ERROR_MESSAGES: Record<SpeechRecognitionErrorCode, string> = {
  'not-allowed': 'Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen.',
  'no-speech': 'Keine Sprache erkannt. Bitte sprechen Sie deutlich.',
  'audio-capture': 'Kein Mikrofon gefunden. Bitte schließen Sie ein Mikrofon an.',
  'network': 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
  'aborted': 'Spracherkennung abgebrochen.',
  'service-not-allowed': 'Spracherkennungsdienst nicht verfügbar.',
  'not-supported': 'Spracherkennung wird in diesem Browser nicht unterstützt.',
};

function getErrorMessage(errorCode: string): SpeechRecognitionError {
  const code = errorCode as SpeechRecognitionErrorCode;
  return {
    error: code,
    message: ERROR_MESSAGES[code] || `Unbekannter Fehler: ${errorCode}`,
  };
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionReturn {
  const {
    language = 'de-DE',
    continuous = false,
    interimResults = true,
    onResult,
    onError,
    onEnd,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [status, setStatus] = useState<SpeechRecognitionStatus>('idle');
  const [error, setError] = useState<SpeechRecognitionError | null>(null);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const isStoppingRef = useRef(false);

  // Check browser support
  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize SpeechRecognition instance
  const getRecognition = useCallback((): WebSpeechRecognition | null => {
    if (!isSupported) return null;

    const SpeechRecognitionClass =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    const recognition = new SpeechRecognitionClass();
    recognition.lang = language;
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;

    return recognition;
  }, [isSupported, language, continuous, interimResults]);

  // Start listening
  const startListening = useCallback(() => {
    if (!isSupported) {
      const err = getErrorMessage('not-supported');
      setError(err);
      setStatus('error');
      onError?.(err);
      return;
    }

    // Stop any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    setError(null);
    setInterimTranscript('');
    isStoppingRef.current = false;

    const recognition = getRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsListening(true);
      setStatus('listening');
    };

    recognition.onresult = (event: WebSpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setTranscript((prev) => prev + finalTranscript);
        onResult?.(finalTranscript, true);
      }

      setInterimTranscript(interim);
      if (interim) {
        onResult?.(interim, false);
      }
    };

    recognition.onerror = (event: WebSpeechRecognitionErrorEvent) => {
      // Ignore 'aborted' errors when we intentionally stopped
      if (event.error === 'aborted' && isStoppingRef.current) {
        return;
      }

      const err = getErrorMessage(event.error);
      setError(err);
      setStatus('error');
      setIsListening(false);
      onError?.(err);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      if (status !== 'error') {
        setStatus('idle');
      }
      onEnd?.();
    };

    try {
      recognition.start();
    } catch (e) {
      const err = getErrorMessage('audio-capture');
      setError(err);
      setStatus('error');
      onError?.(err);
    }
  }, [isSupported, getRecognition, onResult, onError, onEnd, status]);

  // Stop listening
  const stopListening = useCallback(() => {
    isStoppingRef.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setStatus('idle');
    setInterimTranscript('');
  }, []);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  return {
    isListening,
    isSupported,
    status,
    error,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
  };
}

export default useSpeechRecognition;
